import React, { useEffect, useMemo, useRef, useState } from 'react';
import './CityHighlights.css';
import useRevealOnScroll from '../utils/useRevealOnScroll';
import { getHighlightsForCity } from '../utils/cityHighlightsData';
import { getCityHighlights } from '../utils/apiClient';
import { 
  FaUtensils, 
  FaStore, 
  FaMapMarkerAlt, 
  FaFire, 
  FaStar, 
  FaArrowRight,
  FaSearchLocation,
  FaSpinner,
  FaExclamationTriangle,
  FaSyncAlt,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import { GiHotMeal } from 'react-icons/gi';
import { MdRestaurant } from 'react-icons/md';
import { MdLocalDining, MdLocationPin } from 'react-icons/md';
import { BiFoodMenu } from 'react-icons/bi';

export default function CityHighlights({ location, onDetectLocation, isDetecting }) {
  const { ref, isVisible } = useRevealOnScroll({ rootMargin: '0px 0px -8% 0px' });
  const dishesRef = useRef(null);
  const restaurantsRef = useRef(null);
  const [activeTab, setActiveTab] = useState('dishes'); // 'dishes' | 'restaurants'
  const [dishFilter, setDishFilter] = useState(null);

  const cityName = location?.name;

  const [highlights, setHighlights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fallbackHighlights = useMemo(() => getHighlightsForCity(cityName), [cityName]);

  useEffect(() => {
    if (!cityName) {
      setHighlights(null);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getCityHighlights(cityName, { signal: controller.signal });
        setHighlights(data);
      } catch (e) {
        console.error('Failed to load city highlights:', e);
        setError('Unable to load city highlights');
        setHighlights(null);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [cityName]);

  const effectiveHighlights = highlights || fallbackHighlights;
  const dishes = effectiveHighlights?.dishes || [];
  const restaurants = effectiveHighlights?.restaurants || [];

  const selectedDish = dishFilter ? dishes.find((d) => d.id === dishFilter) : null;

  const restaurantsForDish = useMemo(() => {
    if (!dishFilter) return restaurants;
    return restaurants.filter((r) => (r.popularDishes || []).includes(dishFilter));
  }, [restaurants, dishFilter]);

  return (
    <section
      className={`cityHighlights ${isVisible ? 'reveal isVisible' : 'reveal'}`}
      id="city"
      ref={ref}
    >
      <div className="container">
        <div className="cityHighlightsHead">
          <div className="cityHighlightsTitleRow">
            <h2 className="sectionTitle">
              <span className="text-gradient">Popular in {cityName || 'your city'}</span>
              <div className="cityBadge" title={location?.source ? `Source: ${location.source}` : undefined}>
                <FaMapMarkerAlt className="locationIcon" />
                <span>{cityName ? cityName : 'Set Location'}</span>
              </div>
            </h2>
            {!cityName && (
              <button 
                className="detectLocationBtn" 
                onClick={onDetectLocation}
                disabled={isDetecting}
              >
                {isDetecting ? (
                  <>
                    <FaSpinner className="spin" />
                    Detecting...
                  </>
                ) : (
                  <>
                    <FaSearchLocation />
                    Detect My Location
                  </>
                )}
              </button>
            )}
          </div>

          {cityName && (
            <div className="cityStatusRow" aria-live="polite">
              {isLoading ? (
                <span className="cityStatus cityStatusLoading">
                  <FaSpinner className="spin" /> Loading live city highlights...
                </span>
              ) : error ? (
                <div className="coming-soon-container">
                  <div className="coming-soon-content">
                    <h2>
                      Popular in
                      <span className="city-name">{cityName}</span>
                    </h2>
                    <div className="coming-soon-text">
                      <h3>We are coming in your city, <span className="text-gradient">Soon</span></h3>
                    </div>
                  </div>
                  <div className="video-container">
                    <video autoPlay loop muted playsInline>
                      <source src="/animate.webm" type="video/webm" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              ) : highlights ? (
                <div className="cityStatus cityStatusOk">
                  <FaFire className="pulse" />
                  <span>Showing live highlights for {cityName}</span>
                </div>
              ) : (
                <div className="cityStatus cityStatusHint">
                  <FaInfoCircle />
                  <span>Showing sample data for {cityName}. <button className="btnLink" onClick={() => window.location.reload()}>Try again</button></span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="tabsContainer">
          <div className="tabContainer">
            <button
              className={`tabButton ${activeTab === 'dishes' ? 'active' : ''}`}
              onClick={() => setActiveTab('dishes')}
              aria-selected={activeTab === 'dishes'}
              aria-controls="dishes-tab"
            >
              <span className="tabIcon">
                <GiHotMeal />
              </span>
              <span>Trending Dishes</span>
              {activeTab === 'dishes' && <span className="activeIndicator" aria-hidden="true"></span>}
            </button>
            <button
              className={`tabButton ${activeTab === 'restaurants' ? 'active' : ''}`}
              onClick={() => setActiveTab('restaurants')}
              aria-selected={activeTab === 'restaurants'}
              aria-controls="restaurants-tab"
            >
              <span className="tabIcon">
                <MdRestaurant />
              </span>
              <span>Top Restaurants</span>
              {activeTab === 'restaurants' && <span className="activeIndicator" aria-hidden="true"></span>}
            </button>
          </div>
          {dishFilter && (
            <div className="filterContainer">
              <button 
                type="button" 
                className="chip chipGhost" 
                onClick={() => setDishFilter(null)}
              >
                Clear filter
              </button>
            </div>
          )}
        </div>

        <div className="cityHighlightsGrid">
          <div className="cityCol" ref={dishesRef}>
            <div className="cityColHead">
              <h3 className="cityColTitle">Popular dishes</h3>
              <p className="cityColSub">Tap a dish to see where it’s trending.</p>
            </div>

            <div className="cards" role="list">
              {dishes.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={dishFilter === d.id ? 'card cityCard isActive' : 'card cityCard'}
                  onClick={() => {
                    setDishFilter(d.id);
                    setActiveTab('restaurants');
                  }}
                  role="listitem"
                >
                  <div className="cityCardTop">
                    <div className="cityCardTitle">{d.name}</div>
                    {d.tags?.length ? (
                      <div className="tagRow" aria-label="tags">
                        {d.tags.slice(0, 2).map((t) => (
                          <span className="tag" key={t}>
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="cityCardDesc">{d.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="cityCol" ref={restaurantsRef}>
            <div className="cityColHead">
              <h3 className="cityColTitle">Popular restaurants</h3>
              <p className="cityColSub">
                {selectedDish ? (
                  <>
                    Showing places popular for <strong>{selectedDish.name}</strong>.
                  </>
                ) : (
                  'Top picks people visit the most.'
                )}
              </p>
            </div>

            <div className="cards" role="list">
              {restaurantsForDish.map((r) => (
                <div key={r.id} className="card cityCard" role="listitem">
                  <div className="cityCardTop">
                    <div className="cityCardTitle">{r.name}</div>
                    <div className="cityMeta">
                      <FaMapMarkerAlt aria-hidden="true" />
                      {r.area}
                    </div>
                  </div>

                  <div className="tagRow" style={{ marginTop: 10 }}>
                    {(r.popularDishes || [])
                      .map((dishId) => dishes.find((d) => d.id === dishId))
                      .filter(Boolean)
                      .slice(0, 3)
                      .map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          className={dishFilter === d.id ? 'tagBtn tagBtnActive' : 'tagBtn'}
                          onClick={() => {
                            setDishFilter(d.id);
                            setActiveTab('restaurants');
                          }}
                        >
                          {d.name}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cityHighlightsFooter">
          <a
            className="btn btnGhost"
            href="#choose"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById('choose');
              if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
            }}
          >
            Try the demo
          </a>
        </div>
      </div>
    </section>
  );
}
